import { Injectable, Injector, inject } from '@angular/core';
import { ActivatedRouteSnapshot, BaseRouteReuseStrategy, Params, Router } from '@angular/router';

/** Navigation state flag a page's own view-state write carries. */
export const URL_VIEW_STATE_NAV = 'urlViewState';

/**
 * Angular keeps a page alive when only its query string changes, so a click
 * on the sidebar entry of the page already open (`/main/dashboard` from
 * `/main/dashboard?stats=14D`) cleared the URL while the page kept showing
 * the old filters.
 *
 * Here a page is opened afresh when its query string changes from outside —
 * a nav tap, a notification link, the back button — so it reads the URL
 * again and shows exactly what it says (no params = defaults). The page's
 * own filter writes ({@link injectUrlViewState}) carry {@link URL_VIEW_STATE_NAV}
 * and keep the page as it is. Layout routes (anything with a child, like the
 * `/main` shell) are always kept.
 */
@Injectable()
export class ViewStateReuseStrategy extends BaseRouteReuseStrategy {
  // Read lazily: the Router itself depends on this strategy.
  private readonly injector = inject(Injector);

  override shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    if (!super.shouldReuseRoute(future, curr)) return false;
    if (future.firstChild) return true;
    if (sameParams(future.queryParams, curr.queryParams)) return true;
    const navigation = this.injector.get(Router).getCurrentNavigation();
    // Back / forward replays the history entry's saved state, flag included,
    // so the flag only counts on the write itself.
    return (
      navigation?.trigger === 'imperative' && navigation.extras.state?.[URL_VIEW_STATE_NAV] === true
    );
  }
}

function sameParams(a: Params, b: Params): boolean {
  const keys = Object.keys(a);
  return (
    keys.length === Object.keys(b).length && keys.every((key) => String(a[key]) === String(b[key]))
  );
}
